import axios from 'axios';
import type {
  CallToolRequest,
  CallToolResult,
  ReadResourceRequest,
  ReadResourceResult,
} from '@modelcontextprotocol/sdk/types.js';
import { AppBridge } from '@modelcontextprotocol/ext-apps/app-bridge';
import type { McpUiSizeChangedNotification } from '@modelcontextprotocol/ext-apps/app-bridge';
import { CallMcpToolService, ReadMcpResourceService } from '../services/chat';
import { normalizeWidgetToolResult } from './mcp-tool-utils';

const HOST_INFO = { name: 'Grigo', version: '1.0.0' };

type AppBridgeOpenLinkParams = Parameters<NonNullable<AppBridge['onopenlink']>>[0];
type AppBridgeOpenLinkResult = Awaited<ReturnType<NonNullable<AppBridge['onopenlink']>>>;
type AppBridgeCallToolParams = CallToolRequest['params'];
type AppBridgeCallToolResult = CallToolResult;
type AppBridgeReadResourceParams = ReadResourceRequest['params'];
type AppBridgeReadResourceResult = ReadResourceResult;
type AppBridgeSizeChangeParams = McpUiSizeChangedNotification['params'];

export type McpAppBridge = {
  bridge: AppBridge;
  cleanup: () => void;
};

export function toBridgeToolResult(result: unknown): Parameters<AppBridge['sendToolResult']>[0] {
  return normalizeWidgetToolResult(result) as Parameters<AppBridge['sendToolResult']>[0];
}

export function getWidgetErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
      return '';
    }
    const body = err.response?.data as { message?: string; error?: string } | undefined;
    const serverMessage = body?.message || body?.error;
    if (typeof serverMessage === 'string' && serverMessage.trim()) {
      return serverMessage;
    }
    if (err.message === 'Network Error') {
      return 'Failed to load widget UI from the MCP server. The tool may have completed successfully.';
    }
    return err.message || 'Failed to load widget';
  }
  if (err instanceof Error && err.name === 'AbortError') {
    return '';
  }
  return err instanceof Error ? err.message : 'Failed to load widget';
}

export function createAppBridge(
  mcpId: number,
  iframe: HTMLIFrameElement,
  onResize: (height: number) => void,
  cachedResource?: { uri: string; html: string },
): McpAppBridge {
  const bridge = new AppBridge(
    null,
    HOST_INFO,
    {
      openLinks: {},
      serverTools: {},
      serverResources: { listChanged: false },
      logging: {},
      updateModelContext: { text: {} },
    },
    {
      hostContext: {
        theme: 'light',
        platform: 'web',
        displayMode: 'inline',
        availableDisplayModes: ['inline'],
        containerDimensions: { maxHeight: 800 },
      },
    },
  );

  bridge.onopenlink = async ({
    url,
  }: AppBridgeOpenLinkParams): Promise<AppBridgeOpenLinkResult> => {
    window.open(url, '_blank', 'noopener,noreferrer');
    return {};
  };

  bridge.oncalltool = async (params: AppBridgeCallToolParams): Promise<AppBridgeCallToolResult> => {
    const response = await CallMcpToolService(mcpId, params.name, params.arguments || {});
    const payload = response?.data?.data ?? response?.data;
    return normalizeWidgetToolResult(payload) as AppBridgeCallToolResult;
  };

  bridge.onreadresource = async (
    params: AppBridgeReadResourceParams,
  ): Promise<AppBridgeReadResourceResult> => {
    if (cachedResource?.html) {
      return {
        contents: [
          {
            uri: params.uri,
            mimeType: 'text/html;profile=mcp-app',
            text: cachedResource.html,
          },
        ],
      } as AppBridgeReadResourceResult;
    }

    const response = await ReadMcpResourceService(mcpId, params.uri);
    const payload = response?.data?.data ?? response?.data;
    return payload as AppBridgeReadResourceResult;
  };

  bridge.onsizechange = ({ height }: AppBridgeSizeChangeParams) => {
    if (typeof height === 'number' && height > 0) {
      onResize(height);
    }
  };

  const resizeObserver = new ResizeObserver(([entry]) => {
    const width = Math.round(entry.contentRect.width);
    if (width > 0) {
      bridge.sendHostContextChange({
        containerDimensions: { width, maxHeight: 800 },
      });
    }
  });
  resizeObserver.observe(iframe);

  return {
    bridge,
    cleanup: () => {
      resizeObserver.disconnect();
      bridge.onopenlink = undefined;
      bridge.oncalltool = undefined;
      bridge.onreadresource = undefined;
      bridge.onsizechange = undefined;
    },
  };
}
